/**
 * Virtualized transaction list for performance
 */
import { useRef, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { TransactionWithSplits } from '@/services/transactions';
import TransactionRow from './TransactionRow';
import { Spinner } from '@/components/ui';
import { cn } from '@/utils/cn';

export interface TransactionListProps {
  transactions: TransactionWithSplits[];
  selectedIds: Set<string>;
  onSelectTransaction: (id: string) => void;
  onClickTransaction: (transaction: TransactionWithSplits) => void;
  isLoading?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  showCheckboxes?: boolean;
  className?: string;
}

const ITEM_HEIGHT = 72; // Height of each transaction row

/**
 * Virtualized transaction list for efficient rendering
 */
export default function TransactionList({
  transactions,
  selectedIds,
  onSelectTransaction,
  onClickTransaction,
  isLoading = false,
  hasNextPage = false,
  fetchNextPage,
  isFetchingNextPage = false,
  showCheckboxes = false,
  className,
}: TransactionListProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  // Virtualizer setup
  const rowVirtualizer = useVirtualizer({
    count: transactions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 10,
  });

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!parentRef.current || !fetchNextPage || !hasNextPage || isFetchingNextPage) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = parentRef.current;
    const scrollThreshold = 200; // Load more when within 200px of bottom

    if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const items = rowVirtualizer.getVirtualItems();

  // Empty state
  if (!isLoading && transactions.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12', className)}>
        <svg
          className="h-12 w-12 text-gray-300 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <p className="text-gray-500 text-sm">No transactions found</p>
        <p className="text-gray-400 text-xs mt-1">
          Try adjusting your filters or date range
        </p>
      </div>
    );
  }

  // Initial loading state
  if (isLoading && transactions.length === 0) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className={cn('h-full overflow-auto', className)}
      onScroll={handleScroll}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {items.map((virtualRow) => {
          const transaction = transactions[virtualRow.index];
          if (!transaction) return null;
          return (
            <div
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <TransactionRow
                transaction={transaction}
                isSelected={selectedIds.has(transaction.id)}
                onSelect={onSelectTransaction}
                onClick={onClickTransaction}
                showCheckbox={showCheckboxes}
              />
            </div>
          );
        })}
      </div>

      {/* Loading more indicator */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-4">
          <Spinner size="sm" />
          <span className="ml-2 text-sm text-gray-500">Loading more...</span>
        </div>
      )}

      {/* End of list indicator */}
      {!hasNextPage && transactions.length > 0 && (
        <div className="text-center py-4 text-sm text-gray-400">
          End of transactions
        </div>
      )}
    </div>
  );
}
